import type { QueryClient } from '@tanstack/react-query';
import type { GetProcessDraftResponse } from '@bliks-io/contracts';
import { processDraftKeys } from '../../query-keys';

/**
 * Monotonic version counter per (QueryClient × processId). `optimisticDraftUpdate`
 * bumps it before writing; `restoreDraftCache` only restores a `prev` snapshot
 * if the version hasn't advanced since the snapshot was taken. If it has,
 * another optimistic update already landed — restoring would undo newer
 * user work, so we invalidate instead and let the next fetch settle truth.
 */
const versionByClient = new WeakMap<QueryClient, Map<string, number>>();

function bumpVersion(queryClient: QueryClient, processId: string): number {
  let byProcess = versionByClient.get(queryClient);
  if (!byProcess) {
    byProcess = new Map();
    versionByClient.set(queryClient, byProcess);
  }
  const next = (byProcess.get(processId) ?? 0) + 1;
  byProcess.set(processId, next);
  return next;
}

function currentVersion(queryClient: QueryClient, processId: string): number {
  return versionByClient.get(queryClient)?.get(processId) ?? 0;
}

export interface OptimisticDraftContext {
  prev: GetProcessDraftResponse | undefined;
  version: number;
}

/**
 * Cancel in-flight draft queries, snapshot the current cache, and apply an
 * optimistic `updater`. Returns `{ prev, version }` — store in the mutation's
 * context and pass to `restoreDraftCache` from `onError`.
 */
export async function optimisticDraftUpdate(
  queryClient: QueryClient,
  processId: string,
  updater: (prev: GetProcessDraftResponse) => GetProcessDraftResponse
): Promise<OptimisticDraftContext> {
  const key = processDraftKeys.detail(processId);
  await queryClient.cancelQueries({ queryKey: key });
  const prev = queryClient.getQueryData<GetProcessDraftResponse>(key);
  if (!prev) return { prev, version: currentVersion(queryClient, processId) };
  const next = updater(prev);
  if (next === prev) return { prev, version: currentVersion(queryClient, processId) };
  const version = bumpVersion(queryClient, processId);
  queryClient.setQueryData<GetProcessDraftResponse>(key, next);
  return { prev, version };
}

/**
 * Restore a snapshot from `optimisticDraftUpdate`, but only if no newer
 * optimistic update has landed in the meantime. If one has, invalidate the
 * query so the server response becomes truth — preserves in-flight later
 * writes instead of clobbering them with the stale snapshot.
 */
export function restoreDraftCache(
  queryClient: QueryClient,
  processId: string,
  ctx: OptimisticDraftContext | undefined
): void {
  if (!ctx?.prev) return;
  const key = processDraftKeys.detail(processId);
  if (currentVersion(queryClient, processId) !== ctx.version) {
    queryClient.invalidateQueries({ queryKey: key });
    return;
  }
  queryClient.setQueryData<GetProcessDraftResponse>(key, ctx.prev);
}

/**
 * Apply an authoritative-response updater to the draft cache in place. Used
 * by `onSuccess` paths where the server response is the source of truth —
 * no invalidate needed, so we avoid a useless refetch flash.
 */
export function applyDraftCache(
  queryClient: QueryClient,
  processId: string,
  updater: (prev: GetProcessDraftResponse) => GetProcessDraftResponse
): void {
  const key = processDraftKeys.detail(processId);
  const prev = queryClient.getQueryData<GetProcessDraftResponse>(key);
  if (!prev) return;
  const next = updater(prev);
  if (next === prev) return;
  queryClient.setQueryData<GetProcessDraftResponse>(key, next);
}
